/**
 * Connect-flow diagnostics.
 *
 * Dependency-free, fully client-side checks that identify WHY the plugin connect
 * flow is failing on a particular machine. When a connect hangs (especially on a
 * single device that fails in every browser), the cause is almost always local:
 * the device is offline, browser storage/cookies are blocked, a firewall / VPN /
 * antivirus "web shield" / ad-or-privacy extension is blocking our domains, or the
 * system clock is wrong (which breaks HTTPS and auth tokens).
 *
 * Each check returns: { id, label, status: 'pass'|'warn'|'fail', detail, advice? }
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// The /health route lives at the server root, not under /api/v1.
function serverOrigin() {
  try {
    return new URL(API_BASE_URL).origin;
  } catch (_) {
    return API_BASE_URL.replace(/\/api\/v1\/?$/, '');
  }
}

function fetchWithTimeout(url, options = {}, timeoutMs = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timer));
}

// ── Individual checks ─────────────────────────────────────────────────────────

function checkOnline() {
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
  return online
    ? { id: 'online', label: 'Internet connection', status: 'pass', detail: 'Your device reports it is online.' }
    : {
        id: 'online', label: 'Internet connection', status: 'fail',
        detail: 'Your device reports it is offline.',
        advice: 'Reconnect to Wi-Fi or your network and try again.',
      };
}

function checkStorage() {
  const blocked = [];
  try {
    const k = '__iseo_diag__';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
  } catch (_) { blocked.push('site storage'); }
  try {
    const k = '__iseo_diag__';
    window.sessionStorage.setItem(k, '1');
    window.sessionStorage.removeItem(k);
  } catch (_) { blocked.push('session storage'); }
  if (typeof navigator !== 'undefined' && navigator.cookieEnabled === false) {
    blocked.push('cookies');
  }

  if (blocked.length === 0) {
    return { id: 'storage', label: 'Browser storage & cookies', status: 'pass', detail: 'Storage and cookies are available.' };
  }
  return {
    id: 'storage', label: 'Browser storage & cookies', status: 'fail',
    detail: `Blocked: ${blocked.join(', ')}.`,
    advice: 'Sign-in cannot complete while storage is blocked. Turn off Private/Incognito mode, allow cookies & site data for this site, and disable strict "block all cookies" settings or privacy extensions, then try again.',
  };
}

// Returns the check result, plus an internal _date (HTTP Date header) used for clock skew.
async function checkReachable(id, label, url, options) {
  try {
    const res = await fetchWithTimeout(url, options, 8000);
    return { id, label, status: 'pass', detail: `Reachable (HTTP ${res.status}).`, _date: res.headers.get('date') };
  } catch (err) {
    const timedOut = err && err.name === 'AbortError';
    return {
      id, label, status: 'fail',
      detail: timedOut ? 'No response within 8 seconds (timed out).' : 'Could not connect.',
      advice: 'Something on this device or network is blocking the connection. Common causes: a VPN, antivirus or internet-security software with an "SSL/web shield" (Kaspersky, Avast, ESET, Bitdefender…), an ad/privacy browser extension, or a company/school firewall. Try: pause the VPN, turn off the web-shield/extension for this site, or switch networks (e.g. a phone hotspot) and retry.',
    };
  }
}

function checkClock(serverDateHeader) {
  if (!serverDateHeader) {
    return { id: 'clock', label: 'Device clock', status: 'warn', detail: 'Could not verify (server time unavailable).' };
  }
  const skewMs = Math.abs(Date.now() - new Date(serverDateHeader).getTime());
  const skewMin = Math.max(1, Math.round(skewMs / 60000));
  if (skewMs <= 2 * 60000) {
    return { id: 'clock', label: 'Device clock', status: 'pass', detail: 'Clock is accurate.' };
  }
  return {
    id: 'clock', label: 'Device clock',
    status: skewMs > 5 * 60000 ? 'fail' : 'warn',
    detail: `Your device clock is off by about ${skewMin} minute(s).`,
    advice: 'A wrong clock breaks secure (HTTPS) connections and sign-in tokens. Set your date & time to "set automatically" in your system settings, then try again.',
  };
}

// ── Orchestrator ──────────────────────────────────────────────────────────────

/**
 * Run all checks and return { results, overall, summary }.
 *   results: array of { id, label, status, detail, advice? }
 *   overall: 'pass' | 'warn' | 'fail'
 *   summary: the first failing check (the most likely culprit), or null
 */
export async function runConnectDiagnostics() {
  const results = [checkOnline(), checkStorage()];

  const [serverRes, authRes] = await Promise.all([
    checkReachable('server', 'ImproveSEO servers', `${serverOrigin()}/health`, { method: 'GET' }),
    checkReachable('auth', 'Sign-in service', `${SUPABASE_URL}/auth/v1/health`, {
      method: 'GET',
      headers: SUPABASE_ANON_KEY ? { apikey: SUPABASE_ANON_KEY } : {},
    }),
  ]);

  results.push(serverRes, authRes);
  results.push(checkClock(serverRes._date || authRes._date));

  // Strip internal fields before returning
  const clean = results.map(({ _date, ...rest }) => rest);
  const firstFail = clean.find((r) => r.status === 'fail') || null;
  const overall = firstFail ? 'fail' : clean.some((r) => r.status === 'warn') ? 'warn' : 'pass';

  return { results: clean, overall, summary: firstFail };
}
